/**
 * CompactInfoBlock - Reusable block with optional title and per-block edit button
 *
 * Used across all tab components for consistent section styling.
 * The edit button triggers form opening via aircraftCardStore.
 */

import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'

interface CompactInfoBlockProps {
  /** Optional block title (e.g., "Aircraft Specs", "Owner") */
  title?: string
  /** Form ID to open when edit button is clicked */
  formId?: string
  /** Field to pre-focus in the form */
  prefocusField?: string
  /** Custom action element rendered next to the title (e.g., "+" button) */
  titleAction?: React.ReactNode
  /** Block content */
  children: React.ReactNode
  /** Additional CSS class for the container */
  className?: string
}

export function CompactInfoBlock({
  title,
  formId,
  prefocusField,
  titleAction,
  children,
  className = '',
}: CompactInfoBlockProps) {
  const openEditForm = useAircraftCardStore((s) => s.openEditForm)

  return (
    <div className={`py-2 ${className}`}>
      {/* Header with title and edit button */}
      {(title || formId || titleAction) && (
        <div className="flex items-center justify-between mb-1.5">
          {title ? (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {title}
            </span>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-0.5">
            {titleAction}
            {formId && (
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 hover:bg-muted"
                onClick={() => openEditForm(formId, prefocusField)}
                title={`Edit ${title || 'section'}`}
              >
                <Pencil className="w-3 h-3 text-muted-foreground" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Block content */}
      <div className="space-y-1">
        {children}
      </div>
    </div>
  )
}
